import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Table,
  Pagination,
  Form,
  Row,
  Col,
} from "react-bootstrap";
import Typography from "@mui/material/Typography";
import axios from "axios";
import { Notification } from "../types";
import { ArrowLeft } from "lucide-react";

const InstanceNotifications: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalNotifications, setTotalNotifications] = useState(0);
  const [perPage] = useState(50);
  const [instanceName, setInstanceName] = useState<string>("");

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchInstanceName();
    fetchNotifications();
  }, [id, currentPage]);

  const fetchInstanceName = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/trading-instances/${id}`
      );
      setInstanceName(response.data.name);
    } catch (err) {
      console.error("Error fetching instance name:", err);
    }
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${BACKEND_URL}/api/trading-instances/${id}/notifications?page=${currentPage}&per_page=${perPage}`
      );
      setNotifications(response.data.notifications);
      setTotalPages(response.data.total_pages || 1);
      setTotalNotifications(response.data.total || 0);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to fetch notifications");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const formatNotificationMessage = (notification: Notification) => {
    let message = notification.message;

    // If the message already contains UTML badges, return as is
    if (message.includes("[UTML]") && message.includes("[") && message.includes("]")) {
      return message;
    }

    // Otherwise, add algo name if available
    if (notification.linked_algo) {
      message = `[${notification.linked_algo.name}] ${message}`;
    }

    return message;
  };

  const parseNotificationMessage = (notification: Notification) => {
    const message = notification.message;
    
    // Check if this is a UTML notification with badges
    const utmlMatch = message.match(/^📊 \[UTML\] \[([^\]]+)\] \[([^\]]+)\] (.+)/);
    if (utmlMatch) {
      const [, algorithmName, instanceName, restOfMessage] = utmlMatch;
      return {
        isUTML: true,
        algorithmName,
        instanceName,
        message: restOfMessage,
        originalMessage: message
      };
    }
    
    // Check if this is a data fetch success notification
    if (message.includes("Data Fetch Success") && message.includes("📈 Latest Candle OHLC Data:")) {
      return {
        isUTML: true,
        isDataFetch: true,
        message: message,
        originalMessage: message
      };
    }
    
    return {
      isUTML: false,
      message: message,
      originalMessage: message
    };
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center gap-3">
          <Button
            variant="outline-secondary"
            onClick={() => navigate(`/instances/${id}`)}
            className="d-flex align-items-center"
          >
            <ArrowLeft size={16} className="me-1" />
            Back to Instance
          </Button>
          <Typography variant="h4">Notifications - {instanceName}</Typography>
        </div>
        <div className="text-muted">
          <small>Total: {totalNotifications} notifications</small>
        </div>
      </div>

      <Card>
        <Card.Body>
          <Typography variant="h6" className="mb-3">
            All Notifications
          </Typography>

          {notifications.length > 0 ? (
            <>
              <Table responsive>
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Type</th>
                    <th>Priority</th>
                    <th>Algo</th>
                    <th>Message</th>
                    <th>Tags</th>
                  </tr>
                </thead>
                <tbody>
                  {notifications.map((notification) => (
                    <tr key={notification.id}>
                      <td>
                        {new Date(notification.timestamp).toLocaleString()}
                      </td>
                      <td>
                        {notification.notification_type ? (
                          <span className="badge bg-primary">
                            {notification.notification_type}
                          </span>
                        ) : (
                          <span className="text-muted">Unknown</span>
                        )}
                      </td>
                      <td>
                        {notification.priority ? (
                          <span className={`badge ${
                            notification.priority === 'CRITICAL' ? 'bg-danger' :
                            notification.priority === 'HIGH' ? 'bg-warning' :
                            notification.priority === 'NORMAL' ? 'bg-info' : 'bg-secondary'
                          }`}>
                            {notification.priority}
                          </span>
                        ) : (
                          <span className="text-muted">NORMAL</span>
                        )}
                      </td>
                      <td>
                        {notification.linked_algo ? (
                          <span className="badge bg-success">
                            {notification.linked_algo.name}
                          </span>
                        ) : (
                          <span className="text-muted">System</span>
                        )}
                      </td>
                      <td>
                        {(() => {
                          const parsed = parseNotificationMessage(notification);
                          if (parsed.isUTML && parsed.isDataFetch) {
                            // Special handling for data fetch notifications with OHLC
                            return (
                              <div>
                                <div className="mb-2">
                                  <span className="badge bg-primary me-1">UTML</span>
                                  <span className="badge bg-success me-1">Data Fetch</span>
                                  <strong>{parsed.message.split('\n')[0]}</strong>
                                </div>
                                <div className="small text-muted">
                                  <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.8em', margin: 0 }}>
                                    {parsed.message.split('\n').slice(1).join('\n')}
                                  </pre>
                                </div>
                              </div>
                            );
                          } else if (parsed.isUTML) {
                            // UTML notifications with badges
                            return (
                              <div>
                                <div className="mb-1">
                                  <span className="badge bg-primary me-1">UTML</span>
                                  <span className="badge bg-info me-1">{parsed.algorithmName}</span>
                                  <span className="badge bg-secondary me-1">{parsed.instanceName}</span>
                                </div>
                                <Typography variant="body2">
                                  {parsed.message}
                                </Typography>
                              </div>
                            );
                          } else {
                            // Regular notifications
                            return (
                              <Typography variant="body2">
                                {formatNotificationMessage(notification)}
                              </Typography>
                            );
                          }
                        })()}
                      </td>
                      <td>
                        {notification.tags && notification.tags.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1">
                            {notification.tags.slice(0, 3).map((tag, index) => (
                              <span 
                                key={index} 
                                className={`badge ${
                                  tag.startsWith('instance-') ? 'bg-dark' :
                                  tag.startsWith('utml') ? 'bg-primary' :
                                  tag.startsWith('broker-') ? 'bg-info' :
                                  tag.startsWith('algo-') ? 'bg-success' :
                                  tag.startsWith('symbol-') ? 'bg-warning' :
                                  tag.includes('error') || tag.includes('failed') ? 'bg-danger' :
                                  tag.includes('success') ? 'bg-success' :
                                  tag.includes('warning') ? 'bg-warning' :
                                  'bg-secondary'
                                }`}
                                style={{ fontSize: '0.7rem' }}
                                title={tag}
                              >
                                {tag.length > 15 ? `${tag.substring(0, 15)}...` : tag}
                              </span>
                            ))}
                            {notification.tags.length > 3 && (
                              <span className="badge bg-secondary" style={{ fontSize: '0.7rem' }}>
                                +{notification.tags.length - 3}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted">No tags</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <Pagination>
                    <Pagination.First
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    />

                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const page =
                        Math.max(1, Math.min(totalPages - 4, currentPage - 2)) +
                        i;
                      return (
                        <Pagination.Item
                          key={page}
                          active={page === currentPage}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </Pagination.Item>
                      );
                    })}

                    <Pagination.Next
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                </div>
              )}
            </>
          ) : (
            <Typography className="text-muted text-center py-4">
              No notifications available
            </Typography>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default InstanceNotifications;
